import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import type { Dayjs, ConfigType } from 'dayjs';

import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { opTime } from 'utils/dates';
import { OnSubmitArgs, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpFormDrawer } from 'new-components/DLS/OpFormDrawer/OpFormDrawer';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive, heliumSend } from 'utils/helpers';

import { useTimezoneConverter } from '../../../../../hooks/useTimezoneConverter';
import {
  formatFullName,
  transformNullToEmptyString,
} from '../../../../../utils/utils';
import { DATE_TIME_AM_PM_FORMAT } from '../../../../../constants/dates';
import STATUS from '../../../../../constants/status';
import { DRAWER_WIDTH } from '../../../../../constants/ui';
import { VISITOR_INVITATION_CREATED_ID } from '../../../../../constants/userActivities';
import {
  fetchLocations,
  LocationsState,
} from '../../../../../store/slices/locationsSlice';
import {
  fetchInvitationConfig,
  fetchInvitationCustomFields,
  VisitorInvitationState,
} from '../../../../../store/slices/visitorInvitationSlice';
import {
  fetchVisitorTypes,
  VisitorTypesState,
} from '../../../../../store/slices/visitorTypesSlice';
import {
  searchVisitors,
  searchVisitorsToday,
  VisitorSearchState,
} from '../../../../../store/slices/visitorSearchSlice';
import { fetchVisits } from '../../../../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../../../../store/store';
import { Location } from '../../../../../types/locationTypes';
import { VisitorInvite } from '../../../../../types/visitorInvitationTypes';
import {
  ExcludeHostsResponse,
  HostUser,
  UsersResponse,
} from '../../../../../types/visitorsDrawerTypes';
import VisitorList from '../formComponents/VisitorList';

interface InvitationDrawerProps {
  open: boolean;
  onClose: () => void;
  locationIdRef: number;
}

interface NewInvitationResponse {
  data?: Array<{ id: number }> | null;
}

interface ChangedField {
  name?: Array<string | number>;
  value?: unknown;
}

type DayjsInputValue = string | number | Date | Dayjs;

const isDayjsInputValue = (value: unknown): value is DayjsInputValue => {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    value instanceof Date ||
    opTime.isDayjs(value)
  );
};

const InvitationDrawer = ({
  open,
  onClose,
  locationIdRef,
}: InvitationDrawerProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();
  const { toUTC } = useTimezoneConverter(locationIdRef);
  const { orgId } = getOpParams();
  const { userId } = useIdentityAndUser();
  const { isValid: hasVisitorlogWrite } = useValidateScopes([
    'o{orgId}-visitorVisitorLog:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasAllVisitorsWrite } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as NonNullable<LocationsState['globalLocation']>['id'] | undefined;
  const lastSearchParams = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorSearch', 'lastSearchParams']),
  ) as VisitorSearchState['lastSearchParams'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];
  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];
  const invitationCustomFields = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationCustomFields']),
  ) as VisitorInvitationState['invitationCustomFields'];
  const visitorTypes = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorTypes', 'visitorTypes']),
  ) as VisitorTypesState['visitorTypes'];

  const [hosts, setHosts] = useState<HostUser[]>([]);
  const [excludedHostIds, setExcludedHostIds] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [form] = OpForm.useForm();

  const getLocationTimezone = useCallback(
    (locId: number) => {
      const location = locations.find((loc: Location) => loc.id === locId);
      return (
        location?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      );
    },
    [locations],
  );

  const getInitialScheduleTimes = useCallback(
    (locId: number) => {
      const tz = getLocationTimezone(locId);
      const nowInTz = opTime().tz(tz);
      const initialScheduleIn = nowInTz.minute(0).add(1, 'hour');
      const initialScheduleOut = initialScheduleIn.add(1, 'hour');
      // Format as local time string without timezone offset for DatePicker
      return {
        scheduleIn: initialScheduleIn.format('YYYY-MM-DDTHH:mm:ss'),
        scheduleOut: initialScheduleOut.format('YYYY-MM-DDTHH:mm:ss'),
      };
    },
    [getLocationTimezone],
  );

  const [initialValues, setInitialValues] = useState(() => {
    const times = getInitialScheduleTimes(globalLocationId || locationIdRef);
    return {
      locationId: globalLocationId,
      visitorTypeId: visitorTypes.data[0]?.id,
      scheduleIn: times.scheduleIn,
      scheduleOut: times.scheduleOut,
      visitors: [],
    };
  });

  useEffect(() => {
    if (open) {
      const times = getInitialScheduleTimes(locationIdRef);

      setInitialValues({
        locationId: locationIdRef,
        visitorTypeId: visitorTypes.data[0]?.id,
        scheduleIn: times.scheduleIn,
        scheduleOut: times.scheduleOut,
        visitors: [],
      });
    }
  }, [
    open,
    globalLocationId,
    visitorTypes,
    locationIdRef,
    getInitialScheduleTimes,
  ]);

  // Fetch visits and red flags
  useEffect(() => {
    const fetchData = async () => {
      if (!orgId) return;

      setLoading(true);
      try {
        if (invitationConfig === null) {
          dispatch(fetchInvitationConfig({ orgId }));
        }
        if (invitationCustomFields.length === 0) {
          dispatch(fetchInvitationCustomFields({ orgId }));
        }
        if (locations.length === 0) {
          dispatch(fetchLocations({ orgId, status: 1 }));
        }
        if (visitorTypes.data.length === 0) {
          dispatch(fetchVisitorTypes({ orgId }));
        }

        const [users, excludedHosts] = (await Promise.all([
          heliumReceive('listUsers', [orgId]),
          heliumReceive('listAllExcludeHosts', [orgId]),
        ])) as [UsersResponse, ExcludeHostsResponse];

        const activeUsers = (users.data ?? []).filter(
          (user) => user.status === 'A',
        );
        const excludedIds = (excludedHosts.data ?? []).map((ex) => ex.userId);

        setHosts(activeUsers);
        setExcludedHostIds(excludedIds);
      } catch {
        // Failed to fetch data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [orgId, open]);

  const handleSubmit = useCallback(
    async ({ values }: OnSubmitArgs) => {
      setIsSubmitting(true);
      try {
        if (!values.visitors?.length) {
          notificationService.show(
            'error',
            t('invitationDrawer.notification.addVisitorError'),
          );
          return setIsSubmitting(false);
        }

        const data = transformNullToEmptyString(values);

        // Filter out numeric keys directly
        const filteredBody = Object.keys(data).reduce(
          (acc, key) =>
            isNaN(Number(key)) ? { ...acc, [key]: data[key] } : acc,
          {} as Record<string, unknown>,
        );

        const customFields = Object.keys(data).reduce(
          (acc, key) =>
            !isNaN(Number(key)) ? { ...acc, [key]: data[key] } : acc,
          {} as Record<string, unknown>,
        );

        const { scheduleIn, scheduleOut, locationId, ...payload } =
          filteredBody as {
            scheduleIn?: ConfigType;
            scheduleOut?: ConfigType;
            locationId?: number;
            [key: string]: unknown;
          };
        const sIn = opTime(scheduleIn);
        const sOut = opTime(scheduleOut).isSameOrBefore(sIn)
          ? opTime(scheduleOut).add(1, 'day')
          : opTime(scheduleOut);

        const inviteResponse = (await heliumSend('newInvitation', [orgId], {
          ...payload,
          locationId: locationId || locationIdRef,
          scheduleIn: toUTC(sIn),
          scheduleOut: toUTC(sOut),
          visitors: data.visitors.map((visitor: VisitorInvite) => ({
            firstName: visitor.firstName?.trim(),
            middleName: visitor.middleName?.trim(),
            lastName: visitor.lastName?.trim(),
            email: visitor.email?.trim(),
            mobilePhone: visitor.mobilePhone?.trim(),
            status: STATUS.PENDING.id,
          })),
        })) as unknown as NewInvitationResponse;

        const visit = inviteResponse.data?.[0];
        if (!visit?.id) {
          throw new Error('Invitation response is missing visit id');
        }

        await Promise.all(
          Object.entries(customFields).map(([fieldId, fieldValue]) => {
            if (fieldValue !== undefined && fieldValue !== null) {
              return heliumSend('createVisitorData', [orgId, visit.id], {
                fieldId,
                value: fieldValue,
              }).catch(() => {});
            }
            return Promise.resolve();
          }),
        );

        const fullNameList = values.visitors
          .map((visitor: VisitorInvite) =>
            formatFullName(visitor.firstName, null, visitor.lastName),
          )
          .join(', ');

        if (!userId) {
          notificationService.show(
            'warning',
            t('notification.activityLogMasterModeWarning'),
          );
        } else {
          await heliumSend('newActivityLog', [orgId], {
            userId,
            activityId: VISITOR_INVITATION_CREATED_ID,
            details: fullNameList,
          });
        }

        notificationService.show(
          'success',
          t('invitationDrawer.notification.createSuccess'),
        );
        dispatch(fetchVisits({ orgId }));
        dispatch(searchVisitorsToday({ orgId }));
        if (Object.keys(lastSearchParams).length !== 0) {
          dispatch(
            searchVisitors({
              ...lastSearchParams,
              orgId,
              locationId: lastSearchParams.locationId || globalLocationId!,
            }),
          );
        }

        // form.resetFields();
        onClose();
      } catch {
        notificationService.show(
          'error',
          t('invitationDrawer.notification.submissionError'),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      orgId,
      userId,
      t,
      dispatch,
      onClose,
      toUTC,
      globalLocationId,
      lastSearchParams,
      locationIdRef,
    ],
  );

  const handleFieldsChange = (
    changedFields: ChangedField[],
    _allFields: ChangedField[],
  ) => {
    const changedField = changedFields[0];
    if (changedField?.name?.length) {
      const { name, value } = changedField;
      const [field] = name;

      if (field === 'scheduleIn') {
        const scheduleIn = value;
        if (isDayjsInputValue(scheduleIn)) {
          const startMoment = opTime(scheduleIn);
          const newScheduleOut = startMoment.add(1, 'hour');
          form.setFieldsValue({ scheduleOut: newScheduleOut });
        }
      } else if (field === 'scheduleOut') {
        const scheduleOut = value;
        const scheduleIn = form.getFieldValue('scheduleIn');

        if (isDayjsInputValue(scheduleIn) && isDayjsInputValue(scheduleOut)) {
          const startMoment = opTime(scheduleIn);
          const endMoment = opTime(scheduleOut);

          if (startMoment.isSameOrAfter(endMoment)) {
            form.setFieldsValue({ scheduleIn: endMoment.subtract(1, 'hour') });
          }
        }
      }
    }
  };

  const renderFormFields = () => {
    return invitationCustomFields.map((formField, index) => {
      const capitalizedLabel = formField.name;
      const rules = [
        {
          required: formField.required === 1,
          message: t('invitationDrawer.customField.required', {
            customFieldName: formField.name,
          }),
        },
      ];

      if (formField.values) {
        const options = formField.values.map((option) => ({
          label: option.display || option.name,
          value: option.value,
        }));

        return (
          <OpForm.Select
            label={capitalizedLabel}
            name={formField.id}
            options={options}
            key={formField.id || index.toString()}
            rules={rules}
            placeholder={
              formField.placeholder ? formField.placeholder : undefined
            }
            allowClear
          />
        );
      }
      return (
        <OpForm.TextAreaInput
          label={capitalizedLabel}
          name={formField.id}
          key={formField.id || index.toString()}
          rows={4}
          rules={rules}
          placeholder={
            formField.placeholder ? formField.placeholder : undefined
          }
        />
      );
    });
  };

  const filteredHosts = (
    hasAllVisitorsWrite ? hosts : hosts.filter((host) => host.id === userId)
  ).filter((host) => !excludedHostIds.includes(host.id));

  return (
    <OpFormDrawer
      title={t('invitationDrawer.title')}
      size={DRAWER_WIDTH}
      form={form}
      open={open}
      onClose={() => {
        onClose();
      }}
      formComponent={
        <OpForm
          form={form}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onFieldsChange={handleFieldsChange}
          hasError={false}
          defaultButtons={false}
          isLoading={loading || isSubmitting}
        >
          <VisitorList isInviting isLoading={isSubmitting} />

          <OpForm.Select
            label={t('invitationDrawer.visitorType.label')}
            name="visitorTypeId"
            rules={[
              {
                required: true,
                message: t('invitationDrawer.visitorType.required'),
              },
            ]}
            placeholder={t('invitationDrawer.visitorType.placeholder')}
            options={visitorTypes.data.map((type) => ({
              label: t(type.languageCode ?? ''),
              value: type.id,
            }))}
          />

          <OpForm.Select
            label={t('invitationDrawer.siteId.label')}
            name="locationId"
            rules={[
              {
                required: true,
                message: t('invitationDrawer.siteId.required'),
              },
            ]}
            placeholder={t('invitationDrawer.siteId.placeholder')}
            options={locations.map((location: Location) => ({
              label: location.name,
              value: location.id,
            }))}
            disabled
          />

          <OpForm.DatePicker
            format={DATE_TIME_AM_PM_FORMAT}
            showTime
            minuteStep={5}
            name="scheduleIn"
            label={t('invitationDrawer.scheduleIn.label')}
            rules={[
              {
                required: true,
                message: t('invitationDrawer.scheduleIn.required'),
              },
            ]}
          />

          <OpForm.DatePicker
            format={DATE_TIME_AM_PM_FORMAT}
            showTime
            minuteStep={5}
            name="scheduleOut"
            label={t('invitationDrawer.scheduleOut.label')}
            rules={[
              {
                required: true,
                message: t('invitationDrawer.scheduleOut.required'),
              },
            ]}
          />

          {invitationConfig?.visitCompany.included === 1 && (
            <OpForm.Input
              label={t('invitationDrawer.company.label')}
              name="company"
              rules={[
                {
                  required: invitationConfig.visitCompany.required === 1,
                  message: t('invitationDrawer.company.required'),
                },
              ]}
            />
          )}
          {invitationConfig?.host.included === 1 && (
            <OpForm.Select
              label={t('invitationDrawer.host.label')}
              name="hostId"
              rules={[
                {
                  required: invitationConfig.host.required === 1,
                  message: t('invitationDrawer.host.required'),
                },
              ]}
              placeholder={
                filteredHosts.length === 0
                  ? t('invitationDrawer.host.placeholderNotAvailable')
                  : t('invitationDrawer.host.placeholder')
              }
              showSearch={{
                filterOption: (input, option) =>
                  (option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase()),
              }}
              options={filteredHosts.map((host) => ({
                label: host.identity?.fullName || host.identity?.email,
                value: host.id,
              }))}
              allowClear={hasAllVisitorsWrite}
            />
          )}
          {invitationConfig?.visitPurpose.included === 1 && (
            <OpForm.TextAreaInput
              label={t('invitationDrawer.purpose.label')}
              name="purpose"
              rows={4}
              rules={[
                {
                  required: invitationConfig.visitPurpose.required === 1,
                  message: t('invitationDrawer.purpose.required'),
                },
              ]}
            />
          )}
          {invitationConfig?.visitNotes?.included === 1 &&
            hasVisitorlogWrite && (
              <OpForm.TextAreaInput
                label={t('invitationDrawer.note.label')}
                name="note"
                rows={4}
                rules={[
                  {
                    required: invitationConfig.visitNotes.required === 1,
                    message: t('invitationDrawer.note.required'),
                  },
                ]}
              />
            )}
          <OpDivider />
          {renderFormFields()}
        </OpForm>
      }
    />
  );
};

export default InvitationDrawer;
